import { fetchMarketAnalysisTableData } from "api/marketAnalysisApi";
import { createQueryParamsForSSRM } from "./generalHelper";
export const MARKET_ANALYSIS_FILTERS = "market_analysis_filters";

// Helper to aggregate data by month for a series
function aggregateSeriesByMonth(series) {
  const grouped = {};
  series.data.forEach(point => {
    const date = new Date(point.x);
    const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
    if (!grouped[monthKey]) {
      grouped[monthKey] = { x: new Date(date.getFullYear(), date.getMonth(), 1).getTime(), y: 0 };
    }
    grouped[monthKey].y += point.y; // Sum y values for the month
  });
  return {
    ...series,
    data: Object.values(grouped)
  };
}

/**
 * Clones the options to avoid direct mutation based on custom_grouping_type.
 * @param {Object} options - The original options object to be modified.
 * @return {Object} The modified options object with updated formatters.
 */
export const modifyChartOptionsForGrouping = (options) => {
  // Clone the options to avoid direct mutation
  const modifiedOptions = JSON.parse(JSON.stringify(options));

  // Aggregate data by month for monthly grouping
  if (modifiedOptions.custom_grouping_type === "monthly" && Array.isArray(modifiedOptions.series)) {
    modifiedOptions.series = modifiedOptions.series.map(aggregateSeriesByMonth);
    // Inject custom tickPositioner for monthly ticks
    if (modifiedOptions.xAxis) {
      modifiedOptions.xAxis.tickPositioner = function () {
        const positions = [];
        const dataMin = this.dataMin;
        const dataMax = this.dataMax;
        let current = new Date(dataMin);
        current.setDate(1); // Start at the first of the month
        while (current.getTime() <= dataMax) {
          positions.push(current.getTime());
          current.setMonth(current.getMonth() + 1);
        }
        return positions;
      };
      if (!modifiedOptions.xAxis.labels) modifiedOptions.xAxis.labels = {};
      modifiedOptions.xAxis.labels.format = '{value:%b %y}';
    }
  }

  switch (modifiedOptions.custom_grouping_type) {
    case "quarterly":
      modifiedOptions.xAxis.labels.formatter = function () {
        const date = new Date(this.value);
        const quarter = Math.floor(date.getMonth() / 3) + 1;
        return "Q" + quarter + " " + date.getFullYear().toString().slice(-2);
      };
      modifiedOptions.tooltip.formatter = function () {
        const date = new Date(this.point.x);
        const quarter = Math.floor(date.getMonth() / 3) + 1;
        return (
          "<b>" +
          this.series.name +
          "</b><br>x: Q" +
          quarter +
          " " +
          date.getFullYear().toString().slice(-2) +
          "<br>y: " +
          this.point.y
        );
      };
      break;
    case "yearly":
      modifiedOptions.xAxis.labels.formatter = function () {
        const date = new Date(this.value);
        return date.getFullYear().toString();
      };
      modifiedOptions.tooltip.formatter = function () {
        const date = new Date(this.point.x);
        return (
          "<b>" +
          this.series.name +
          "</b><br>x: " +
          date.getFullYear().toString() +
          "<br>y: " +
          this.point.y
        );
      };
      break;
    case "monthly":
      modifiedOptions.xAxis.labels.formatter = function () {
        const date = new Date(this.value);
        const month = date.toLocaleString("default", { month: "short" });
        return month + "-" + date.getFullYear().toString().slice(-2);
      };
      modifiedOptions.tooltip.formatter = function () {
        const date = new Date(this.point.x);
        const month = date.toLocaleString("default", { month: "short" });
        return (
          "<b>" +
          this.series.name +
          "</b><br>x: " +
          month +
          "-" +
          date.getFullYear().toString().slice(-2) +
          "<br>y: " +
          this.point.y
        );
      };
      break;
    case "daily":
      modifiedOptions.xAxis.labels.formatter = function () {
        const date = new Date(this.value);
        // Using English locale and specifying day, month, and year options
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "2-digit",
        });
      };
      modifiedOptions.tooltip.formatter = function () {
        const date = new Date(this.point.x);
        // Using English locale and specifying day, month, and year options
        return (
          "<b>" +
          this.series.name +
          "</b><br>x: " +
          date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "2-digit",
          }) +
          "<br>y: " +
          this.point.y
        );
      };
      break;

    default:
      // Reset formatters for other grouping modes
      delete modifiedOptions.xAxis.labels.formatter;
      modifiedOptions.tooltip.formatter = undefined;
      break;
  }

  return modifiedOptions;
};

const generateFilterParamsDataSource = (
  params,
  sort_model,
  start_date,
  end_date,
  filterModel
) => {
  const requestParams = params.request;
  const startRow = requestParams.startRow;
  const endRow = requestParams.endRow;
  const groupKeys = JSON.stringify(requestParams.groupKeys || []);

  const sortModel = sort_model
    ? JSON.stringify(sort_model)
    : JSON.stringify(requestParams.sortModel || []);

  const queryParams = createQueryParamsForSSRM(
    startRow,
    endRow,
    sortModel,
    groupKeys,
    filterModel
  );

  let dateFilter = {
    start_date,
    end_date,
  };

  return { queryParams, dateFilter };
};

export const createServerSideDataSource = ({
  filterModelData,
  sort_model,
  start_date,
  end_date,
}) => {
  return {
    getRows: (params) => {
      const filterModel = JSON.stringify({
        ...filterModelData,
        ...(params.request.filterModel || []),
      });

      const { queryParams, dateFilter } = generateFilterParamsDataSource(
        params,
        sort_model,
        start_date,
        end_date,
        filterModel
      );

      fetchMarketAnalysisTableData({ ...queryParams, ...dateFilter })
        .then((response) => {
          params.success({
            rowData: response.data.data.rows,
            rowCount: response.data.data.lastRow,
          });
        })
        .catch((error) => {
          console.error("Error fetching market-analysis data:", error);
          params.fail();
        });
    },
  };
};

export const createServerDataSourceParams = {
  gridRef: null,
  filterModelData: null,
  sort_model: null,
  start_date: null,
  end_date: null,
};

export function createServerDataSource(createServerDataSourceParams) {
  const { gridRef } = createServerDataSourceParams;
  delete createServerDataSourceParams.gridRef;
  console.log(createServerDataSourceParams);
  if (!createServerDataSourceParams.start_date || !createServerDataSourceParams.end_date) {
    return;
  }
  gridRef.current.api?.setServerSideDatasource(
    createServerSideDataSource({
      ...createServerDataSourceParams,
    })
  );
}

const requiredFields = [
  "table_id",
  "product_contract_style_id",
  "start_date",
  "end_date",
  "metric",
  "period_type_id",
  "analysis_style",
  "show_historic_vol",
  "rolling_avg_days",
];

export function validateTimeSeriesFilters(data) {
  // Check if all required fields are filled (not null, undefined, or empty string)
  for (let field of requiredFields) {
    if (
      data[field] === null ||
      data[field] === undefined ||
      data[field] === ""
    ) {
      return false;
    }
  }

  // Special conditions for metric
  if (data.metric === "delta") {
    if (!data.x_axis) {
      return false;
    }
  } else if (data.metric === "strike") {
    if (!data.strike && data.strike !== 0) {
      return false;
    }
  }

  // Special conditions for analysis_style
  if (data.analysis_style === "tenor") {
    if (!data.style_period_tenor_id) {
      return false;
    }
  } else {
    if (!data.period_number && data.period_number !== 0) {
      return false;
    }
  }

  return true;
}

/**
 * Validates an array of time series filters
 * @param {Array} filtersArray - Array of time series filter objects
 * @returns {boolean} - True if all filters in the array are valid, false otherwise
 */
export function validateTimeSeriesFiltersArray(filtersArray) {
  if (!Array.isArray(filtersArray) || filtersArray.length === 0) {
    return false;
  }

  // Check if all filters in the array are valid
  return filtersArray.every((filter) => validateTimeSeriesFilters(filter));
}

export function validateAndGenerateParams(queryParams) {
  if (!Array.isArray(queryParams) || queryParams.length === 0) {
    return null; // Ensure queryParams is an array and not empty
  }

  const params = new URLSearchParams();
  const isValid = queryParams.every((filter, index) => {
    // First validate the filter structure
    if (!validateTimeSeriesFilters(filter)) {
      return false;
    }

    // Create a copy of the filter to avoid modifying the original
    const processedFilter = { ...filter };

    // Handle metric-specific parameters
    if (processedFilter.metric === "delta") {
      if (!processedFilter.x_axis) {
        return false;
      }
      processedFilter.metric_value = processedFilter.x_axis;
      delete processedFilter.strike;
      delete processedFilter.x_axis;
    } else if (processedFilter.metric === "strike") {
      if (processedFilter.strike == null) {
        return false;
      }
      processedFilter.metric_value = processedFilter.strike;
      delete processedFilter.x_axis;
      delete processedFilter.strike;
    }

    // Handle analysis style specific parameters
    if (processedFilter.analysis_style === "tenor") {
      if (!processedFilter.style_period_tenor_id) {
        return false;
      }
      delete processedFilter.period_number;
    } else {
      if (processedFilter.period_number == null) {
        return false;
      }
      delete processedFilter.style_period_tenor_id;
    }

    // Add all valid parameters to URLSearchParams
    Object.entries(processedFilter).forEach(([key, value]) => {
      if (value != null && value !== "") {
        params.append(`filters[${index}][${key}]`, value);
      }
    });

    return true;
  });

  return isValid ? params.toString() : null;
}

export function handleTimeSeriesTableFilters(queryParams) {
  if (queryParams.metric === "delta") {
    queryParams.metric_value = queryParams.x_axis;
  } else if (queryParams.metric === "strike") {
    queryParams.metric_value = queryParams.strike;
  }

  if (queryParams.analysis_style === "tenor") {
    delete queryParams.period_number;
  } else if (queryParams.analysis_style === "period") {
    delete queryParams.style_period_tenor_id;
  }

  delete queryParams.x_axis;
  delete queryParams.strike;
}
